import { Link } from 'react-router';
import { useEffect, useRef } from 'react';
import { NAVIGATION, withFinds } from '@/config/site';
import { showsFinds, useFindsLive } from '@/client/lib/useFindsLive';
import { useUI } from '@/client/ui/UIContext';
import { useI18n } from '@/client/i18n/I18nContext';
import { CloseIcon } from '@/components/Icons';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { CaroWordmark } from '@/components/Logo';
import { lp } from '@/client/i18n/locale';

function isExternal(href: string) {
  return href.startsWith('http');
}

/**
 * The site menu opened by the header hamburger, at every width: full screen on
 * a phone, a left-hand panel over the page on desktop (click outside or Esc to
 * close). Lists every section, sub-shops included.
 */
export function MobileNavigation() {
  const { navOpen, closeNav } = useUI();
  const { t } = useI18n();
  const label = (item: { label: string; key?: string }) =>
    item.key ? t(item.key) : item.label;

  const closeRef = useRef<HTMLButtonElement>(null);
  // «Hallazgos» only while a find is public (hidden while unknown).
  const findsShown = showsFinds(useFindsLive());

  useEffect(() => {
    if (navOpen) {
      // Move focus into the dialog, and give it back to whatever opened it (the
      // hamburger) on close, so keyboard users don't land on <body>.
      const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      closeRef.current?.focus();
      const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeNav();
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = '';
        if (opener?.isConnected) opener.focus();
      };
    }
    return;
  }, [navOpen, closeNav]);

  if (!navOpen) return null;

  return (
    <>
      {/* Behind the desktop panel: click outside to close (covered on a phone). */}
      <div className="overlay" onClick={closeNav} aria-hidden />
      <div className="mobile-nav" role="dialog" aria-modal="true" aria-label={t('a11y.menu')}>
        <div className="header__inner">
          <span className="brand">
            <CaroWordmark className="brand__mark" title="CARO." />
            <span className="brand__sub">Casa de estilo</span>
          </span>
          <button
            ref={closeRef}
            type="button"
            className="icon-btn"
            aria-label={t('a11y.menuClose')}
            onClick={closeNav}
          >
            <CloseIcon />
          </button>
        </div>
        <ul>
          {NAVIGATION.map((item) => (
            <li key={item.href} className="mobile-nav__group">
              {isExternal(item.href) ? (
                <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={closeNav}>
                  {label(item)}
                </a>
              ) : (
                <Link to={lp(item.href)} onClick={closeNav}>
                  {label(item)}
                </Link>
              )}
              {item.children ? (
                <div className="mobile-nav__children">
                  {withFinds(item.children, findsShown).map((child) => (
                    <Link key={child.href} to={lp(child.href)} onClick={closeNav}>
                      {label(child)}
                    </Link>
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        <div style={{ marginTop: 'var(--space-6)' }}>
          <LanguageSwitcher />
        </div>
      </div>
    </>
  );
}
